import { UseFormSetError } from "react-hook-form";

/**
 * Zod 호환 API 에러 응답 타입
 */
export interface ApiErrorResponse {
  error: string;
  message: string;
  issues: Array<{
    path: string[];
    message: string;
    code: string;
  }>;
}

type DRFError = {
  [key: string]: string[];
} & { non_field_errors?: string[] };


export function handleDRFErrors(errors: DRFError, setError: UseFormSetError<any>, setGlobalError?: (msg: string) => void) {
  Object.entries(errors).forEach(([key, messages]) => {
    if (key === "non_field_errors") {
      setGlobalError?.(messages[0]);
    } else {
      // console.log(`🐸 ${key} ${messages}`)
      setError(key, { type: "server", message: messages[0] });
    }
  });
}

/**
 * React Hook Form의 setError에 사용 가능한 형식으로 변환
 * @param apiError API 에러 응답
 * @returns 필드명 -> 에러 메시지 맵
 */
export function convertApiErrorsToFieldErrors(apiError: ApiErrorResponse): Record<string, string> {
  const fieldErrors: Record<string, string> = {};

  for (const issue of apiError.issues) {
    if (issue.path.length > 0) {
      // path[0]이 필드명
      const fieldName = issue.path[0];
      fieldErrors[fieldName] = issue.message;
    }
  }

  return fieldErrors;
}

/**
 * API 응답을 Zod 호환 에러 객체로 파싱
 * @param response 서버 응답
 * @returns ApiErrorResponse 또는 null
 */
export async function parseApiError(response: Response): Promise<ApiErrorResponse | null> {
  try {
    const data = await response.json();

    // error 필드가 있으면 Zod 호환 포맷
    if (data.error && Array.isArray(data.issues)) {
      return {
        error: data.error,
        message: data.message || '오류가 발생했습니다.',
        issues: data.issues,
      };
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * React Hook Form의 setError에 직접 사용하기 위한 변환
 * @param fieldErrors convertApiErrorsToFieldErrors 결과
 * @returns react-hook-form setError에 전달할 수 있는 객체
 */
export function formatFieldErrorsForSetError(fieldErrors: Record<string, string>) {
  const formattedErrors: Record<string, { message: string }> = {};

  for (const [fieldName, message] of Object.entries(fieldErrors)) {
    formattedErrors[fieldName] = { message };
  }

  return formattedErrors;
}

/**
 * 한 줄로 API 에러를 처리하는 함수
 * @param response 서버 응답
 * @param setError react-hook-form setError
 * @returns 처리 여부
 */
export async function handleApiError(
  response: Response,
  setError: (fieldName: string, error: { message: string }) => void
): Promise<boolean> {
  const apiError = await parseApiError(response);

  if (!apiError) {
    return false;
  }

  const fieldErrors = convertApiErrorsToFieldErrors(apiError);

  for (const [fieldName, message] of Object.entries(fieldErrors)) {
    setError(fieldName, { message });
  }

  return true;
}
